import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.serialization.json.JsonObject

@Composable
private fun TourStandingRank(rank: Int?, highlightedPlaces: Int = 10) {
    when {
        rank == null -> Text("—")
        rank in 1..highlightedPlaces -> RankBadge(rank, prizePlaces = highlightedPlaces)
        else -> Text(rank.toString())
    }
}

@Composable
fun TourStandings(token: String? = null) {
    TourSeasonScope(token, showSeason = false, includeFinished = true) { season, tournaments ->
        key(season.id) { TourStandingsBody(token, season, tournaments) }
    }
}

@Composable
private fun TourStandingsBody(token: String?, season: TourSeasonDto, scheduled: List<TourTournamentDto>) {
    var data by remember { mutableStateOf<JsonObject?>(null) }
    var final by remember { mutableStateOf(season.status == "finished") }
    var women by remember { mutableStateOf(false) }
    var detail by remember { mutableStateOf<String?>(null) }
    var tie by remember { mutableStateOf<String?>(null) }
    var order by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var reason by remember { mutableStateOf("") }
    var confirmClose by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var busy by remember { mutableStateOf(false) }
    var retry by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()
    suspend fun reload() { data = TourSeasonResultsApi.standings(token, season.id, final) }
    LaunchedEffect(final, retry) {
        busy = true; error = null; data = null; tie = null; confirmClose = false
        try { reload() } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { busy = false }
    }
    fun operate(action: suspend () -> Unit) { if (!busy) scope.launch {
        busy = true; error = null
        try { action(); tie = null; confirmClose = false; reload() }
        catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { busy = false }
    } }
    val current = data
    val rows = current?.objects("rows").orEmpty()
    val tournaments = current?.objects("tournaments").orEmpty()
    val groups = TOUR_DISCIPLINES.map { (discipline, label) -> label to
        List(2) { index -> tournaments.filter { it.text("discipline") == discipline }.getOrNull(index) }
    }
    val femaleRanks = rows.filter { it.text("gender") == "female" && it.flag("eligible") }
        .mapIndexedNotNull { index, row ->
            if (row.text("rank").toIntOrNull() != null) row.text("player_id") to index + 1 else null
        }.toMap()
    Div(attrs = { classes("card") }) {
        H2 { Text("Celoroční pořadí · ${season.name}") }
        Button(attrs = { if (busy) disabled(); onClick { women = !women } }) { Text(if (women) "Všichni hráči" else "Pouze ženy") }
        if (token != null && season.status != "finished") Button(attrs = { if (busy) disabled(); onClick { final = !final } }) { Text(if (final) "Zobrazit průběžné pořadí" else "Náhled konečného pořadí") }
        if (busy) P { Text("Načítám pořadí…") }
        TourError(error)
        if (error != null) Button(attrs = { onClick { retry++ } }) { Text("Načíst znovu") }
        if (current != null && rows.isEmpty()) P { Text("Zatím nejsou zveřejněné výsledky turnaje.") }
        if (rows.isNotEmpty()) Div(attrs = { classes("table-scroll") }) { Table(attrs = { classes("public-table", "tour-score-table", "tour-standings-table"); attr("aria-label", "Celoroční pořadí Tour") }) {
            Thead {
                Tr {
                    Th(attrs = { rowspan(2); attr("scope", "col") }) { Text("Pořadí celkově") }
                    if (women) Th(attrs = { rowspan(2); attr("scope", "col") }) { Text("Pořadí žen") }
                    Th(attrs = { rowspan(2); attr("scope", "col") }) { Text("Hráč") }
                    Th(attrs = { rowspan(2); attr("scope", "col") }) { Text("Body") }
                    groups.forEach { (label, _) -> Th(attrs = { colspan(2); attr("scope", "colgroup"); classes("tour-group-start") }) { Text(label) } }
                    Th(attrs = { rowspan(2); attr("scope", "col"); classes("tour-group-start") }) { Text("Prémie") }
                    Th(attrs = { rowspan(2); attr("scope", "col") }) { Text("300") }
                }
                Tr { groups.forEach { (_, columns) -> columns.forEachIndexed { index, t -> Th(attrs = {
                    attr("scope", "col"); if (index == 0) classes("tour-group-start")
                    t?.let { attr("title", it.text("title") + it.text("starts_on").takeIf { it.isNotBlank() }?.let { date -> " · ${formatDate(date)}" }.orEmpty()) }
                }) {
                    val number = t?.let { item -> scheduled.firstOrNull { it.id == item.text("id") }?.sequenceNo ?: (tournaments.indexOf(item) + 1) }
                    Text(number?.let { "$it." } ?: "—")
                } } } }
            }
            Tbody { rows.filter { !women || it.text("gender") == "female" }.forEach { row -> Tr {
                Td(attrs = { classes("tour-rank-cell"); attr("title", tourStandingStatus(row)) }) { TourStandingRank(row.text("rank").toIntOrNull()) }
                if (women) Td(attrs = { classes("tour-rank-cell") }) { TourStandingRank(femaleRanks[row.text("player_id")], highlightedPlaces = 3) }
                Td(attrs = { classes("tour-player-name") }) { Span(attrs = {
                    classes("tour-player-link"); tabIndex(0); attr("role", "button"); attr("aria-haspopup", "dialog")
                    onClick { detail = row.text("player_id") }
                    onKeyDown { if (it.key in listOf("Enter", " ")) { it.preventDefault(); detail = row.text("player_id") } }
                }) { Text(row.text("player_name")) } }
                Td { Span(attrs = { classes("pill") }) { Text(row.text("points")) } }
                groups.forEach { (_, columns) -> columns.forEachIndexed { index, t -> Td(attrs = { if (index == 0) classes("tour-group-start") }) {
                    val result = row.objects("results").firstOrNull { it.text("tournament_id") == t?.text("id") }
                    if (result == null) Text("—") else Span(attrs = { if (!result.flag("selected")) classes("tour-discarded"); attr("title", if (result.flag("selected")) "Započítaný výsledek" else "Nezapočítaný výsledek") }) { Text(result.text("points") + if (result.flag("joker")) " J" else "") }
                } } }
                Td(attrs = { classes("tour-group-start"); if (row.flag("awards_pending")) attr("title", "Rozpis starších prémií se doplňuje") }) { Text(row.text("award_count").ifBlank { if (row.flag("awards_pending")) "—" else "0" }) }
                Td { Text(row.text("special_count").ifBlank { "0" }) }
            } } }
        } }
        if (current != null) Div(attrs = { classes("tour-standings-legend", "muted") }) {
            P { Text("Započítává se nejlepší turnaj každé disciplíny a nejlepší zbývající výsledek. Joker násobí pouze základní body. Ve finálním pořadí mají pořadové číslo jen hráči se všemi čtyřmi disciplínami.") }
            P { Text("Tabulku lze posouvat do stran. Kliknutím na hráče zobrazíte rozpis bodů. Přeškrtnuté body se nezapočítávají. J = Joker. Shody: body → silnější koncová HDCP skupina (F až A) → nižší součet umístění započítaných turnajů → los.") }
            P { Text("Prémie a výkony 300 se sčítají ze všech zveřejněných turnajů, včetně bodově nezapočítaných. Sloupec 300 zahrnuje také výkony 20 v Narubáku.") }
        }
    }
    rows.firstOrNull { it.text("player_id") == detail }?.let { row -> TourStandingPlayerDialog(row) { detail = null } }
    if (current == null) return
    if (token != null && !current.flag("finished")) {
        rows.filter { it.flag("tie_pending") && !it.flag("unknown_handicap") }.groupBy { it.text("tie_key") }.forEach { (key, group) -> Div(attrs = { classes("card") }) {
            P { Text("Úplná shoda: ${group.joinToString { it.text("player_name") }}") }
            if (tie != key) Button(attrs = { if (busy) disabled(); onClick { tie = key; order = emptyMap(); reason = "" } }) { Text("Zapsat výsledek losu") }
            else {
                group.forEach { row -> TourChoice("Vylosované pořadí: ${row.text("player_name")}", order[row.text("player_id")] ?: "", listOf("" to "Vyberte pořadí") + group.indices.map { (it+1).toString() to "${it+1}." }, busy) { order = order + (row.text("player_id") to it) } }
                TourField("Způsob losování", reason, busy) { reason = it }
                val valid = group.all { order[it.text("player_id")]?.toIntOrNull() in 1..group.size } && order.values.toSet().size == group.size && reason.isNotBlank()
                Button(attrs = { if (busy || !valid) disabled(); onClick { operate { TourSeasonResultsApi.draw(token, season.id, final, current.text("fingerprint"), group.sortedBy { order[it.text("player_id")]!!.toInt() }.map { it.text("player_id") }, reason) } } }) { Text("Potvrdit los") }
            }
        } }
        if (final) Div(attrs = { classes("card") }) {
            P { Text("Uzavření ročníku vyžaduje dokončené a zveřejněné výsledky všech turnajů a rozhodnuté shody. Po uzavření se ročník již neupravuje.") }
            val ready = tournaments.isNotEmpty() && tournaments.all { it.flag("published") && it.text("status") == "finished" } && rows.none { it.flag("tie_pending") }
            Button(attrs = { if (busy || !ready) disabled(); onClick { confirmClose = true } }) { Text("Uzavřít ročník") }
            if (confirmClose) Button(attrs = { if (busy) disabled(); onClick { operate { TourSeasonResultsApi.close(token, season.id, current.text("fingerprint")) } } }) { Text("Potvrzuji uzavření ročníku") }
        }
    }
}
